import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Modal,
  KeyboardAvoidingView,
  Platform,
  Linking,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { spacing, fontSize, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { parseDecimalInput } from '../i18n/decimalInput';
import { api, ApiError } from '../services/api';
import { formatMoney } from '../data/currencies';
import { formatDisplayCount } from '../i18n/formatCount';
import PageHeader from '../components/PageHeader';
import ScreenEdgeBack from '../components/ChatEdgeBack';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { InfoButton, InfoText, useInfo } from '../components/InfoHint';
import ProfileSection from '../components/ProfileSection';
import { profilePalette } from '../theme/profileSection';
import { uppercaseForDisplayLanguage } from '../utils/locale';

type Transaction = {
  amount: number;
  receiver_amount: number | null;
  currency: string;
  type: string;
  status: string;
  created_at: string;
  from_user_id: number | null;
  from_name: string | null;
  to_name: string | null;
};

type PayoutRequest = {
  id: number;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  processed_at: string | null;
};

type Props = {
  token: string;
  myId: number;
  onBack: () => void;
};

export default function WalletScreen({ token, myId, onBack }: Props) {
  const colors = useTheme();
  const stripeInfo = useInfo();
  const insets = useSafeAreaInsets();
  // 7.14 · D-550 — başlık yüzüyor. Bu ekranda dört ayrı sayfa yüzeyi var
  // (hesap, ödeme talepleri, hareketler, ana sayfa) ve dördü de aynı boşluğu
  // tepede geri vermek zorunda. Sayfanın ÜSTÜNE açılan iki tabaka bunu ALMAZ:
  // tabaka başlığı örter, altından geçmez — kendi güvenli alanı yeter.
  const ustBosluk = screenHeaderContentInset(insets.top);
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [balance, setBalance] = useState<number>(0);
  const [pending, setPending] = useState<number>(0);
  const [earnings, setEarnings] = useState({ totalEarned: 0, inEscrow: 0, withdrawable: 0 });
  const [currency, setCurrency] = useState<string>('USD');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [topupModal, setTopupModal] = useState(false);
  const [customTopup, setCustomTopup] = useState(false);
  const [topupInput, setTopupInput] = useState('');
  const [topupSubmitting, setTopupSubmitting] = useState(false);
  const topupLock = useRef(false);
  const topupAmount = parseDecimalInput(topupInput, lang);
  const validTopup = (amount: number | null): amount is number => amount !== null
    && Number.isFinite(amount) && amount > 0 && amount <= 1000
    && Math.abs(amount * 100 - Math.round(amount * 100)) < 1e-8;
  const topupDisabled = topupSubmitting || !validTopup(topupAmount);
  const [payoutModal, setPayoutModal] = useState(false);
  const [payoutInput, setPayoutInput] = useState('');
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);
  const payoutAmount = parseDecimalInput(payoutInput, lang);
  const payoutDisabled = payoutSubmitting || payoutAmount === null || payoutAmount <= 0 || payoutAmount > pending;
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
  const [stripeOnboarded, setStripeOnboarded] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [view, setView] = useState<'main' | 'account' | 'payouts' | 'transactions'>('main');

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    api.getWallet(token)
      .then((data) => {
        setBalance(Number(data.balance));
        setPending(Number(data.pending));
        setEarnings(data.earnings);
        setCurrency(data.currency);
        setTransactions(data.transactions);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
    api.getPayoutRequests(token).then(setPayoutRequests).catch(() => {});
    api.getStripeStatus(token).then((s) => setStripeOnboarded(s.onboarded)).catch(() => {});
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Test modu (3.4.1) — gerçek ödeme/payout H11 avukat teyidi olmadan
  // açılmıyor, bu yalnızca Stripe Connect Express hesap onboarding'i.
  // Kullanıcı IBAN/kimlik bilgisini Stripe'ın kendi sayfasında giriyor,
  // bize hiç uğramıyor.
  async function connectStripe() {
    setStripeConnecting(true);
    try {
      const { url } = await api.startStripeOnboarding(token);
      await Linking.openURL(url);
    } catch {
      Alert.alert(t('common.error'), t('wallet.stripeConnectFailed'));
    } finally {
      setStripeConnecting(false);
    }
  }

  function addFunds() {
    if (topupLock.current) return;
    setCustomTopup(false);
    setTopupInput('');
    setTopupModal(true);
  }

  function openPayoutModal() {
    setPayoutInput('');
    setPayoutModal(true);
  }

  async function submitPayout(amount: number) {
    if (payoutSubmitting || !Number.isFinite(amount) || amount <= 0 || amount > pending) return;
    setPayoutSubmitting(true);
    try {
      await api.requestPayout(token, amount);
      setPayoutModal(false);
      Alert.alert(t('wallet.payoutRequested'), t('wallet.payoutRequestedBody', { amount: formatMoney(amount, currency, lang) }));
      load();
    } catch {
      Alert.alert(t('common.error'), t('wallet.payoutFailed'));
    } finally {
      setPayoutSubmitting(false);
    }
  }

  function payoutStatusLabel(status: string): string {
    if (status === 'paid') return t('wallet.payoutStatusPaid');
    if (status === 'rejected') return t('wallet.payoutStatusRejected');
    return t('wallet.payoutStatusPending');
  }

  async function topUp(amount: number) {
    if (topupLock.current || !validTopup(amount)) return;
    topupLock.current = true;
    setTopupSubmitting(true);
    try {
      await api.topUpWallet(token, amount, currency);
      setTopupModal(false);
      load();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'currency_changed') {
        setTopupModal(false);
        setTopupInput('');
        load();
      }
      Alert.alert(t('common.error'), t('wallet.addFundsFailed'));
    } finally {
      topupLock.current = false;
      setTopupSubmitting(false);
    }
  }

  function label(tx: Transaction): string {
    if (tx.type === 'topup') return t('wallet.topupLabel');
    const isSender = tx.from_user_id === myId;
    // LEFT JOIN names can be absent; keep supplied names literal, including spacing.
    const name = isSender ? tx.to_name : tx.from_name;
    const person = name?.trim() ? name : t('notifications.centerUnknownPerson');
    if (tx.status === 'escrow') return `${person} — ${t(isSender ? 'wallet.waiting' : 'wallet.incomingRequest')}`;
    if (tx.status === 'released') return `${person} — ${t(isSender ? 'wallet.replied' : 'wallet.earned')}`;
    if (tx.status === 'refunded') return t('wallet.refunded');
    return tx.type;
  }

  function sign(tx: Transaction): string {
    if (tx.status === 'refunded') return '+';
    if (tx.status === 'released' && tx.from_user_id !== myId) return '+';
    return tx.from_user_id === myId ? '-' : '+';
  }

  // Kazanç kalemleri (alınan, topup değil) host'un netini gösterir: brüt amount
  // değil, komisyon düşülmüş receiver_amount (%91). Gönderilen/iade/topup brüt kalır.
  function displayAmount(tx: Transaction): number {
    const isEarning = tx.from_user_id !== myId && tx.type !== 'topup' && tx.receiver_amount != null;
    return isEarning ? Number(tx.receiver_amount) : Number(tx.amount);
  }

  // Koşullu kazanç: alınan ama henüz escrow'da (cevap verilmedi) — kazanılmış
  // değil, 7 günde iade olabilir. Listede soluk gösterilir (kazanılan yeşilden ayrı).
  function isConditional(tx: Transaction): boolean {
    return tx.status === 'escrow' && tx.from_user_id !== myId;
  }

  function headerTitle(): string {
    if (view === 'account') return t('wallet.paymentAccount');
    if (view === 'payouts') return t('wallet.payoutRequests');
    if (view === 'transactions') return t('wallet.transactions');
    return t('wallet.title');
  }

  return (
    <ScreenEdgeBack onBack={view === 'main' ? onBack : () => setView('main')}>
    <View style={styles.container}>
      <PageHeader title={headerTitle()} onBack={view === 'main' ? onBack : () => setView('main')} floating />

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.black} />
        </View>
      ) : failed ? (
        <View style={styles.center}>
          <Text style={styles.errorText}>{t('wallet.failed')}</Text>
          <TouchableOpacity style={styles.retryBtn} onPress={load}>
            <Text style={styles.retryText}>{t('common.retry')}</Text>
          </TouchableOpacity>
        </View>
      ) : view === 'account' ? (
        <View style={[styles.subPage, { paddingTop: ustBosluk }]}>
          {/* 7.6 / D-656 — kenardan kenara satır yerine profildeki bölüm. */}
          <ProfileSection testID="wallet-account-section">
          <TouchableOpacity
            style={styles.row}
            disabled={stripeOnboarded || stripeConnecting}
            onPress={connectStripe}>
            <View style={styles.stripeRowText}>
              <View style={styles.stripeTitleRow}>
                <Text style={styles.stripeRowTitle}>{t('wallet.stripeConnect')}</Text>
                {/* 7.14 / D-654 — açıklama ⓘ arkasında; bağlıyken durum görünür kalır. */}
                {!stripeOnboarded && (
                  <InfoButton open={stripeInfo.open} onPress={stripeInfo.toggle} subject={t('wallet.stripeConnect')} testID="wallet-stripe-info" />
                )}
              </View>
              {stripeOnboarded && <Text style={styles.stripeRowHint}>{t('wallet.stripeConnected')}</Text>}
            </View>
            {stripeConnecting ? (
              <ActivityIndicator color={colors.black} />
            ) : (
              <Text style={stripeOnboarded ? styles.stripeRowStatus : styles.rowChevron}>{stripeOnboarded ? '✓' : '›'}</Text>
            )}
          </TouchableOpacity>
          <InfoText open={stripeInfo.open && !stripeOnboarded} text={t('wallet.stripeConnectInfo')} testID="wallet-stripe-info-text" />
          </ProfileSection>
        </View>
      ) : view === 'payouts' ? (
        payoutRequests.length > 0 ? (
          <ScrollView
            contentContainerStyle={{ paddingTop: ustBosluk, paddingBottom: spacing.xl }}
            scrollIndicatorInsets={{ top: ustBosluk }}>
            <ProfileSection testID="wallet-payouts-section" separated>
              {payoutRequests.map((pr) => (
                <View key={pr.id} style={styles.row}>
                  <Text style={styles.txLabel}>{payoutStatusLabel(pr.status)}</Text>
                  <Text style={styles.txAmount}>{formatMoney(pr.amount, pr.currency, lang)}</Text>
                </View>
              ))}
            </ProfileSection>
          </ScrollView>
        ) : (
          <Text style={[styles.empty, { paddingTop: ustBosluk + spacing.xl }]}>{t('wallet.noPayouts')}</Text>
        )
      ) : view === 'transactions' ? (
        // Sunucu en son 20 işlemi veriyor (routes/wallet.ts); sanal listeye gerek
        // yok, satırlar diğer sayfalar gibi bir bölümün içinde.
        transactions.length > 0 ? (
          <ScrollView
            contentContainerStyle={{ paddingTop: ustBosluk, paddingBottom: spacing.xl }}
            scrollIndicatorInsets={{ top: ustBosluk }}>
            <ProfileSection testID="wallet-transactions-section" separated>
              {transactions.map((item, i) => (
                <View key={i} style={styles.row}>
                  <Text style={styles.txLabel}>{label(item)}</Text>
                  <Text style={[
                    styles.txAmount,
                    isConditional(item) ? styles.conditional
                      : sign(item) === '+' ? styles.positive : styles.negative,
                  ]}>
                    {sign(item)}{formatMoney(displayAmount(item), item.currency, lang)}
                  </Text>
                </View>
              ))}
            </ProfileSection>
          </ScrollView>
        ) : (
          <Text style={[styles.empty, { paddingTop: ustBosluk + spacing.xl }]}>{t('wallet.noTransactions')}</Text>
        )
      ) : (
        <ScrollView
          contentContainerStyle={{ paddingTop: ustBosluk }}
          scrollIndicatorInsets={{ top: ustBosluk }}>
          <View style={styles.balanceCard}>
            <Text style={styles.balanceLabel}>{t('wallet.availableBalance')}</Text>
            <Text style={styles.balanceAmount}>{formatMoney(balance, currency, lang)}</Text>
            <View style={styles.balanceActions}>
              <TouchableOpacity testID="wallet-add-funds" style={[styles.addButton, styles.balanceButton]} onPress={addFunds}>
                <Text testID="wallet-add-funds-label" style={styles.addButtonText} numberOfLines={2}>{t('wallet.addFunds')}</Text>
              </TouchableOpacity>
              <TouchableOpacity testID="wallet-request-payout" style={[styles.payoutButton, styles.balanceButton]} onPress={openPayoutModal}>
                <Text testID="wallet-request-payout-label" style={styles.payoutButtonText} numberOfLines={2}>{t('wallet.requestPayout')}</Text>
              </TouchableOpacity>
            </View>
          </View>

          {(earnings.totalEarned > 0 || earnings.inEscrow > 0 || earnings.withdrawable > 0) && (
            <ProfileSection
              testID="wallet-earnings-section"
              header={<Text style={styles.sectionHeader}>{uppercaseForDisplayLanguage(t('wallet.earningsTitle'), lang)}</Text>}>
              <View style={styles.earningsRow}>
                <Text style={styles.earningsLabel}>{t('wallet.earningsTotal')}</Text>
                <Text style={styles.earningsValue}>{formatMoney(earnings.totalEarned, currency, lang)}</Text>
              </View>
              <View style={styles.earningsRow}>
                <Text style={styles.earningsLabel}>{t('wallet.earningsWithdrawable')}</Text>
                <Text style={[styles.earningsValue, styles.positive]}>{formatMoney(earnings.withdrawable, currency, lang)}</Text>
              </View>
              {earnings.inEscrow > 0 && (
                <View style={styles.earningsRow}>
                  <Text style={styles.earningsLabel}>{t('wallet.earningsEscrow')}</Text>
                  <Text style={styles.earningsValueMuted}>{formatMoney(earnings.inEscrow, currency, lang)}</Text>
                </View>
              )}
            </ProfileSection>
          )}

          <ProfileSection testID="wallet-categories-section" separated>
            <TouchableOpacity style={styles.row} onPress={() => setView('account')}>
              <Text style={styles.catTitle}>{t('wallet.paymentAccount')}</Text>
              <View style={styles.catRight}>
                {stripeOnboarded && <Text style={styles.catMeta}>{t('wallet.stripeConnected')}</Text>}
                <Text style={styles.rowChevron}>›</Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={styles.row} onPress={() => setView('payouts')}>
              <Text style={styles.catTitle}>{t('wallet.payoutRequests')}</Text>
              <View style={styles.catRight}>
                {payoutRequests.length > 0 && <Text style={styles.catMeta}>{formatDisplayCount(payoutRequests.length, lang)}</Text>}
                <Text style={styles.rowChevron}>›</Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={styles.row} onPress={() => setView('transactions')}>
              <Text style={styles.catTitle}>{t('wallet.transactions')}</Text>
              <View style={styles.catRight}>
                {transactions.length > 0 && <Text style={styles.catMeta}>{formatDisplayCount(transactions.length, lang)}</Text>}
                <Text style={styles.rowChevron}>›</Text>
              </View>
            </TouchableOpacity>
          </ProfileSection>
        </ScrollView>
      )}

      <Modal visible={topupModal} animationType="slide" transparent onRequestClose={() => { if (!topupLock.current) setTopupModal(false); }}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={[styles.sheetOverlay, { paddingTop: insets.top }]}>
          <View style={styles.sheet}>
            <ScrollView
              testID="wallet-topup-scroll"
              keyboardShouldPersistTaps="handled"
              style={styles.sheetScroll}
              contentContainerStyle={[styles.sheetContent, { paddingBottom: insets.bottom + spacing.xl }]}>
              <Text testID="wallet-topup-currency" style={styles.sheetTitle}>{t('wallet.addFunds')} · {currency}</Text>
              <Text style={styles.sheetHint}>{t('wallet.addFundsDemo')}</Text>
              {[10, 50, 100].map((amt) => (
                <TouchableOpacity key={amt} testID={`wallet-topup-${amt}`} accessibilityRole="button" disabled={topupSubmitting} style={[styles.sheetOption, topupSubmitting && styles.disabledButton]} onPress={() => topUp(amt)}>
                  <Text style={styles.sheetOptionText}>{formatMoney(amt, currency, lang)}</Text>
                </TouchableOpacity>
              ))}
              <TouchableOpacity testID="wallet-topup-other" accessibilityRole="button" accessibilityState={{ expanded: customTopup, disabled: topupSubmitting }} disabled={topupSubmitting} style={[styles.sheetOption, topupSubmitting && styles.disabledButton]} onPress={() => setCustomTopup(true)}>
                <Text style={styles.sheetOptionText}>{t('wallet.otherAmount')}</Text>
              </TouchableOpacity>
              {customTopup && <View>
                <Text style={styles.sheetHint}>{t('wallet.customAmountHint', { amount: formatMoney(1000, currency, lang) })}</Text>
                <TextInput testID="wallet-topup-input" accessibilityLabel={t('wallet.customAmount')} style={styles.payoutInput}
                  value={topupInput} onChangeText={setTopupInput} keyboardType="decimal-pad" editable={!topupSubmitting}
                  maxLength={20} autoFocus placeholder={t('wallet.customAmount')} placeholderTextColor={colors.gray400} />
                <TouchableOpacity testID="wallet-topup-save" accessibilityRole="button" accessibilityState={{ disabled: topupDisabled, busy: topupSubmitting }} disabled={topupDisabled}
                  style={[styles.sheetOption, topupDisabled && styles.disabledButton]} onPress={() => { if (validTopup(topupAmount)) topUp(topupAmount); }}>
                  {topupSubmitting ? <ActivityIndicator color={colors.white} /> : <Text style={styles.sheetOptionText}>{t('wallet.addFunds')}</Text>}
                </TouchableOpacity>
              </View>}
              <TouchableOpacity testID="wallet-topup-cancel" accessibilityRole="button" disabled={topupSubmitting} style={styles.sheetCancel} onPress={() => setTopupModal(false)}>
                <Text style={styles.sheetCancelText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={payoutModal} animationType="slide" transparent onRequestClose={() => setPayoutModal(false)}>
        <KeyboardAvoidingView
          testID="wallet-payout-keyboard"
          style={styles.keyboardFill}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={[styles.sheetOverlay, { paddingTop: insets.top }]}>
            <View style={styles.sheet}>
              <ScrollView
                testID="wallet-payout-scroll"
                style={styles.sheetScroll}
                contentContainerStyle={[styles.sheetContent, { paddingBottom: insets.bottom + spacing.xl }]}
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="on-drag">
                <Text style={styles.sheetTitle}>{t('wallet.requestPayout')}</Text>
                <Text style={styles.sheetHint}>
                  {t('wallet.payoutHint', { amount: formatMoney(pending, currency, lang) })}
                </Text>
                <TextInput
                  testID="wallet-payout-input"
                  style={styles.payoutInput}
                  value={payoutInput}
                  onChangeText={setPayoutInput}
                  keyboardType="decimal-pad"
                  placeholder="0"
                  placeholderTextColor={colors.gray400}
                  autoFocus
                />
                <TouchableOpacity
                  testID="wallet-payout-all"
                  accessibilityRole="button"
                  style={styles.sheetOption}
                  onPress={() => setPayoutInput(String(pending))}>
                  <Text style={styles.sheetOptionText}>{t('wallet.payoutAll')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="wallet-payout-save"
                  accessibilityRole="button"
                  accessibilityState={{ disabled: payoutDisabled, busy: payoutSubmitting }}
                  style={[styles.addButton, payoutDisabled && styles.disabledButton]}
                  disabled={payoutDisabled}
                  onPress={() => {
                    if (payoutAmount !== null) return submitPayout(payoutAmount);
                  }}>
                  <Text style={styles.addButtonText}>{t('common.save')}</Text>
                </TouchableOpacity>
                <TouchableOpacity testID="wallet-payout-cancel" accessibilityRole="button" style={styles.sheetCancel} onPress={() => setPayoutModal(false)}>
                  <Text style={styles.sheetCancelText}>{t('common.cancel')}</Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
    </ScreenEdgeBack>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  keyboardFill: { flex: 1 },
  container: { flex: 1, backgroundColor: colors.white },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  errorText: { fontSize: fontSize.md, color: colors.gray400, marginBottom: spacing.md },
  retryBtn: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    backgroundColor: colors.black,
  },
  retryText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
  sheetOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  sheet: {
    maxHeight: '100%',
    flexShrink: 1,
    backgroundColor: colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  sheetScroll: { flexGrow: 0 },
  sheetContent: { padding: spacing.lg },
  sheetTitle: { fontSize: fontSize.lg, fontWeight: '700', color: colors.black, textAlign: 'center', marginBottom: spacing.xs },
  sheetHint: { fontSize: fontSize.sm, color: colors.gray400, textAlign: 'center', marginBottom: spacing.lg },
  sheetOption: {
    backgroundColor: colors.black,
    paddingVertical: spacing.md,
    borderRadius: radius.control,
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  sheetOptionText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
  sheetCancel: { paddingVertical: spacing.md, alignItems: 'center', marginTop: spacing.xs },
  sheetCancelText: { color: colors.gray600, fontSize: fontSize.md },
  // 7.6 / D-656 — bölümlerle aynı hizada ve aynı dış köşede (ProfileSection frame).
  balanceCard: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: spacing.xl,
    backgroundColor: colors.black,
    borderRadius: radius.layerOuter,
    alignItems: 'center',
  },
  balanceLabel: { fontSize: fontSize.sm, color: colors.gray400, marginBottom: spacing.xs },
  balanceAmount: { fontSize: 40, fontWeight: '700', color: colors.white, marginBottom: spacing.xs },
  pending: { fontSize: fontSize.sm, color: colors.gray400, marginBottom: spacing.md },
  // 7.6 / D-656 — profildeki bölüm başlığıyla aynı (OwnProfileScreen pricesTitle).
  sectionHeader: {
    flexShrink: 1,
    fontSize: fontSize.sm,
    fontWeight: '600',
    color: profilePalette(colors).heading,
    letterSpacing: 0.5,
  },
  // Profildeki satır ve ok ölçüsü (priceRow, rowChevron).
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    minHeight: 48,
  },
  rowChevron: { flexShrink: 0, width: 12, textAlign: 'center', fontSize: fontSize.md, color: colors.gray600, marginLeft: spacing.sm },
  earningsRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', minHeight: 44 },
  earningsLabel: { flex: 1, minWidth: 0, marginRight: spacing.md, fontSize: fontSize.md, color: colors.gray600 },
  earningsValue: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
  earningsValueMuted: { fontSize: fontSize.md, fontWeight: '600', color: colors.gray400 },
  balanceActions: { width: '100%', flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  balanceButton: {
    flex: 1,
    minWidth: 0,
    minHeight: 44,
    paddingHorizontal: spacing.sm,
  },
  addButton: {
    minHeight: 44,
    backgroundColor: colors.white,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xl,
    borderRadius: radius.control,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: { fontSize: fontSize.md, fontWeight: '600', color: colors.black, textAlign: 'center' },
  disabledButton: { opacity: 0.5 },
  payoutButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.white,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xl,
    borderRadius: radius.control,
    alignItems: 'center',
    justifyContent: 'center',
  },
  payoutButtonText: { fontSize: fontSize.md, fontWeight: '600', color: colors.white, textAlign: 'center' },
  payoutInput: {
    fontSize: fontSize.xl,
    color: colors.black,
    textAlign: 'center',
    borderBottomWidth: 1,
    borderBottomColor: colors.gray200,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },



  stripeRowText: { flex: 1, marginRight: spacing.sm },
  stripeRowTitle: { fontSize: fontSize.md, color: colors.black, flexShrink: 1 },
  stripeTitleRow: { flexDirection: 'row', alignItems: 'center' },
  stripeRowHint: { fontSize: fontSize.sm, color: colors.gray400, marginTop: 2 },
  stripeRowStatus: { fontSize: fontSize.lg, color: colors.gray600 },

  txRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
  },
  txLabel: { flex: 1, minWidth: 0, marginRight: spacing.md, fontSize: fontSize.md, color: colors.black },
  txAmount: { flexShrink: 0, fontSize: fontSize.md, fontWeight: '600' },
  positive: { color: '#22C55E' },
  negative: { color: colors.black },
  conditional: { color: colors.gray400 },
  empty: { fontSize: fontSize.md, color: colors.gray400, textAlign: 'center', paddingTop: spacing.xl },
  subPage: { flex: 1 },

  catTitle: { flex: 1, minWidth: 0, marginRight: spacing.md, fontSize: fontSize.md, color: colors.black },
  catRight: { flexShrink: 0, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  catMeta: { fontSize: fontSize.sm, color: colors.gray400 },
});
